import "./FixtureBoxScore.css";

import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { ISoccerMatch } from "../../../../types";
import {
  BoxScoreDetails,
  MatchInfo,
  MatchStatus,
  ScoreBoard,
} from "./FixtureBoxScoreComponents";
import { useAppStore } from "../../stores/useAppStore";
import { useMatchResult, useMediaQuery } from "../../hooks";
import { buildNavigationUrl } from "../../utils";
import { getTournamentStats } from "../../api";
import { TOURNAMENT_ID } from "../../config/tournament";

export function FixtureBoxScore() {
  const soccerMatch = (useAppStore((state) => state.matchData) ||
    {}) as ISoccerMatch;

  const { isMatchNotStarted } = useMatchResult({ soccerMatch });
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const isHome = useLocation().pathname === "/";
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const handleStatsClick = () => {
    // On desktop home, swap the box-score for the tournament-stats panel
    // below the carousel via `?view=tournament-stats`. Elsewhere (mobile, or
    // any non-home route) fall back to the standalone /tournament-stats page.
    if (isHome && isDesktop) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("view", "tournament-stats");
        return next;
      });
      return;
    }
    navigate(buildNavigationUrl("/tournament-stats"));
  };

  const { data: tournamentStats } = useQuery({
    queryKey: ["tournament-stats", TOURNAMENT_ID],
    queryFn: () => getTournamentStats(TOURNAMENT_ID),
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: Boolean(soccerMatch?.status) && isMatchNotStarted,
  });

  const summary = tournamentStats?.summary;

  if (!soccerMatch?.id) {
    return null;
  }

  return (
    <div className={`fixture-box-score-container fixture-box-score-container--prematch${isMatchNotStarted ? "" : " fixture-box-score-container--live"}`}>
      <div className="fixture-box-score-prematch-main-card">
        {isMatchNotStarted && <div className="fixture-box-score-prematch-bg" />}
        <MatchStatus status={soccerMatch.status} prematch={isMatchNotStarted} soccerMatch={soccerMatch} />
        <ScoreBoard soccerMatch={soccerMatch} />
        <BoxScoreDetails soccerMatch={soccerMatch} />
      </div>
      <div className="fixture-box-score-prematch-info-card">
        <MatchInfo soccerMatch={soccerMatch} />
      </div>
      {/* Tournament Stats — Figma node 2426:12920 — pre-match only */}
      {isMatchNotStarted && (
        <div className="tournament-stats">
          <div className="tournament-stats-form">
            <p className="tournament-stats-title">Tournament stats</p>
            <div className="tournament-stats-card">
              <div className="tournament-stat-item">
                <p className="tournament-stat-label">Top Scorer</p>
                <p className="tournament-stat-value">
                  {summary?.topScorer ? `${summary.topScorer.name} - ${summary.topScorer.value}` : "—"}
                </p>
              </div>
              <div className="tournament-stat-item">
                <p className="tournament-stat-label">Top Assists</p>
                <p className="tournament-stat-value">
                  {summary?.topAssist ? `${summary.topAssist.name} - ${summary.topAssist.value}` : "—"}
                </p>
              </div>
              <div className="tournament-stat-item">
                <p className="tournament-stat-label">Most Clean Sheets</p>
                <p className="tournament-stat-value">
                  {summary?.mostCleanSheets ? `${summary.mostCleanSheets.name} - ${summary.mostCleanSheets.value}` : "—"}
                </p>
              </div>
            </div>
          </div>
          <button className="tournament-stats-cta" onClick={handleStatsClick}>
            <span>View full stats</span>
            <img className="tournament-stats-cta-icon" src="/images/arrow-right-white.svg" alt="" />
          </button>
        </div>
      )}
    </div>
  );
}
